import React, { useState, useEffect, useContext, useMemo } from "react";
import { Users, Video, Play, X, AlertTriangle, Shuffle } from "lucide-react";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import { DailyCallContext } from "../App.jsx";
import { VideoChat } from "../components/VideoChat.jsx";
import { Heartbeat } from "../components/Heartbeat.jsx";

// Client-side mirror of the server's three chunking strategies. Same rules;
// same inputs. Used to render the preview inside the assignment modal so the
// player starting the game can see exactly what will happen before confirming.
function chunkExact(members, P) {
  const n = members.length;
  const fullGroups = Math.floor(n / P);
  const games = [];
  for (let i = 0; i < fullGroups; i++) games.push(members.slice(i * P, (i + 1) * P));
  return { games, leftovers: members.slice(fullGroups * P) };
}

function chunkPartial(members, P) {
  const n = members.length;
  if (n === 0) return { games: [], leftovers: [] };
  if (n === 1) return { games: [], leftovers: [members[0]] };
  if (n <= P) return { games: [members], leftovers: [] };

  const fullGroups = Math.floor(n / P);
  const remainder = n % P;

  if (remainder === 0) {
    const games = [];
    for (let i = 0; i < fullGroups; i++) games.push(members.slice(i * P, (i + 1) * P));
    return { games, leftovers: [] };
  }

  if (remainder >= 2) {
    const games = [];
    for (let i = 0; i < fullGroups; i++) games.push(members.slice(i * P, (i + 1) * P));
    games.push(members.slice(fullGroups * P));
    return { games, leftovers: [] };
  }

  // remainder === 1
  if (P === 2) {
    const games = [];
    for (let i = 0; i < fullGroups; i++) games.push(members.slice(i * P, (i + 1) * P));
    return { games, leftovers: [members[fullGroups * P]] };
  }

  const games = [];
  for (let i = 0; i < fullGroups - 1; i++) games.push(members.slice(i * P, (i + 1) * P));
  const lastFullStart = (fullGroups - 1) * P;
  games.push(members.slice(lastFullStart, lastFullStart + P - 1));
  games.push(members.slice(lastFullStart + P - 1));
  return { games, leftovers: [] };
}

function chunkOverfill(members, P) {
  const partial = chunkPartial(members, P);
  if (partial.leftovers.length === 0) return partial;
  if (partial.games.length === 0) return partial;
  const games = partial.games.map(g => g.slice());
  games[games.length - 1] = games[games.length - 1].concat(partial.leftovers);
  return { games, leftovers: [] };
}

// Fisher–Yates shuffle, returns a new array. Used by Random Assign so the
// auto-fill is genuinely random rather than roster-order.
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Deterministic avatar color from a player id, so the same person keeps the
// same color across renders and across the roster/room views.
function colorForId(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 60% 48%)`;
}

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
}

// A colored circle with the player's initials. `empty` renders a dashed
// placeholder slot instead — an open seat they can drop someone into.
function Avatar({ player, empty = false, size = "w-9 h-9 text-xs" }) {
  if (empty || !player) {
    return (
      <div
        className={`${size} rounded-full border-2 border-dashed border-gray-300 flex-shrink-0`}
      />
    );
  }
  return (
    <div
      className={`${size} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}
      style={{ backgroundColor: colorForId(player.id) }}
      title={player.displayName}
    >
      {initials(player.displayName)}
    </div>
  );
}

// Selectable avatar+name token for a single player. Draggable, so the starting player can
// either click-select-then-click-a-room, or just drag the chip onto a room.
function PlayerChip({ player, selected, onClick, onDragStart, onDragEnd }) {
  return (
    <button
      type="button"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={
        "flex items-center gap-2 w-full text-left rounded-lg border px-2 py-1.5 transition-colors cursor-grab active:cursor-grabbing " +
        (selected
          ? "border-blue-500 ring-2 ring-blue-400 bg-blue-50"
          : "border-gray-200 bg-white hover:bg-gray-50")
      }
    >
      <Avatar player={player} />
      <span className="text-sm text-gray-800 truncate">
        {player.displayName}
        {player.isSelf && <span className="text-gray-400"> (You)</span>}
      </span>
    </button>
  );
}

// Shown when a player has no scenario in their link, or the server flagged the
// scenario as invalid. Rendered instead of the lobby — these players are never
// assigned to a game, so they never reach the exit steps. Also reused by Game
// for role-data failures that surface after the lobby, at game start.
// Stands in for "no group name was supplied". Deliberately not "default": that
// is a name a participant may legitimately type, and the two must stay
// distinguishable — an absent name means an incomplete link and is refused
// below, a chosen one is honoured. Must match NO_GROUP_NAME in
// server/src/callbacks.js.
const NO_GROUP_NAME = "null061486";

// Must match lobbyGroupName() in server/src/callbacks.js exactly — the server
// keys every waiting-room roster entry on this string.
function lobbyGroupName(groupName, scenario) {
  return `${groupName || NO_GROUP_NAME}\u0000${scenario || ""}`;
}

// Where this player believes they belong. Both values arrive as URL params and a
// player cannot get in without them, but the player attributes are written
// asynchronously after connect — so we read the URL, which is there on first
// render, and prefer the player attributes once they land (they are what the
// server keys the roster on, so preferring them is what makes the two converge).
function useLobbyIdentity() {
  const player = usePlayer();
  const { groupName: contextGroupName } = useContext(DailyCallContext);
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const displayGroupName =
    player?.get("groupName") || urlParams.get("groupName") || contextGroupName || NO_GROUP_NAME;
  const scenario = player?.get("scenario") || urlParams.get("scenario") || "";

  return { displayGroupName, scenario, groupKey: lobbyGroupName(displayGroupName, scenario) };
}

// Shown while the server's view of this player has not yet caught up with ours.
// Normally a fraction of a second; the hint appears if it drags, so a genuinely
// broken link reads as a problem rather than as a hang.
function LobbyLoadingPanel() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 15000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="h-screen w-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Joining your group…</h2>
        <p className="text-gray-600">One moment.</p>
        {slow && (
          <p className="text-sm text-gray-500 mt-4">
            This is taking longer than usual. If it doesn't clear, your session link may be
            incomplete — please use the link provided for your session.
          </p>
        )}
      </div>
    </div>
  );
}

export function ScenarioErrorPanel({ message }) {
  return (
    <div className="h-screen w-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-center mb-4">
          <AlertTriangle className="w-12 h-12 text-amber-500" strokeWidth={1.5} />
        </div>
        <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
          Can't join this session
        </h2>
        <p className="text-center text-gray-600">{message}</p>
      </div>
    </div>
  );
}

// Thin wrapper: validate the player's scenario before rendering the lobby. This
// keeps the guard out of CustomLobbyInner's hook list (the wrapper only ever calls
// usePlayer), so the Rules of Hooks are respected across renders.
export function CustomLobby() {
  const player = usePlayer();
  const game = useGame();
  const { displayGroupName, groupKey } = useLobbyIdentity();

  // The server validates the scenario by fetching its role JSON from the club
  // and sets `scenarioError` when it's missing/unknown.
  const scenarioError = player?.get("scenarioError");

  if (scenarioError) {
    return <ScenarioErrorPanel message={scenarioError} />;
  }

  // No group name in the link. Unlike a pending state this never resolves, so it
  // is an error rather than a wait. "default" is not used as the stand-in
  // precisely so that a participant who deliberately types "default" is let
  // through here.
  if (displayGroupName === NO_GROUP_NAME) {
    return (
      <ScenarioErrorPanel message="No group name was specified in your link. Please use the link provided for your session." />
    );
  }

  // Don't render a lobby until the server agrees which lobby we are in.
  //
  // Our roster entry is written on connect, before the client has had a chance
  // to send groupName and scenario, and is rewritten as each arrives. Rendering
  // during that window shows the server's stale view: our own group appears
  // empty because nobody's entry has converged yet, and a Start pressed then
  // would leave the un-converged players behind. Waiting makes the invariant
  // simple — if you can see the lobby, the server and you agree on your group
  // and scenario, and so does everyone you can see.
  //
  // The heartbeat lives here, above that gate, and not inside CustomLobbyInner.
  // The server sweep prunes anyone whose heartbeat goes stale and only re-adds
  // players who are heartbeating, so a heartbeat that stops the moment the entry
  // disappears can never bring it back: one gap (a WiFi blip, a refresh, a slow
  // intro) would strand the player on the loading panel for good.
  const myEntry = game?.get("waitingPlayers")?.[player?.id];
  if (!myEntry || myEntry.groupName !== groupKey) {
    return (
      <>
        <Heartbeat />
        <LobbyLoadingPanel />
      </>
    );
  }

  return (
    <>
      <Heartbeat />
      <CustomLobbyInner />
    </>
  );
}

function CustomLobbyInner() {
  const player = usePlayer();
  const game = useGame();

  // The functional lobby identity: group name AND scenario, so two people who
  // typed the same group name but opened different scenario links are in
  // different lobbies. The wrapper above has already confirmed the server's
  // roster agrees with this before rendering us.
  const {
    displayGroupName: myDisplayGroupName,
    scenario: myScenario,
    groupKey: myGroupName,
  } = useLobbyIdentity();

  // Get waitingPlayers from game (stored by server since usePlayers() doesn't work in lobby)
  const waitingPlayersObj = game?.get("waitingPlayers") || {};

  // Filter players to show only those in the same group (excluding self)
  const groupMembers = useMemo(() => {
    const members = [];
    for (const [playerId, playerInfo] of Object.entries(waitingPlayersObj)) {
      const theirGroupName = playerInfo.groupName;
      const isInMyGroup = theirGroupName === myGroupName;
      const isNotMe = playerId !== player?.id;

      if (isInMyGroup && isNotMe) {
        members.push({ id: playerId, ...playerInfo });
      }
    }
    return members;
  }, [waitingPlayersObj, myGroupName, player?.id]);

  // Calculate total group members and whether game can start. Any group member
  // can start once there are 2+ players.
  const totalGroupMembers = groupMembers.length + 1; // +1 for current player
  const canStartGame = totalGroupMembers >= 2;

  // Real per-game size, surfaced by the server on the waiting game so the
  // lobby can preview how the group would be split. In a multi-treatment batch
  // the size depends on the player's scenario, so prefer the per-scenario value.
  const scenarioSizes = game?.get("scenarioSizes") || {};
  const scenarioNames = game?.get("scenarioNames") || {};
  const scenarioTitle = scenarioNames[myScenario] || myScenario;
  const gamePlayerCount = scenarioSizes[myScenario] || game?.get("gamePlayerCount") || 4;

  // Create a Set of player IDs for video filtering
  const groupMemberIds = useMemo(() => {
    const ids = new Set();
    groupMembers.forEach(p => ids.add(p.id));
    return ids;
  }, [groupMembers]);

  // Flat roster (self first) for the assignment modal: id + display name, plus
  // an `isSelf` flag marking which one is the viewer.
  const myDisplayName = player?.get("displayName");
  const assignmentPlayers = useMemo(() => {
    const list = [];
    if (player?.id) {
      list.push({ id: player.id, displayName: myDisplayName || "You", isSelf: true });
    }
    for (const p of groupMembers) {
      list.push({ id: p.id, displayName: p.displayName || "Anonymous", isSelf: false });
    }
    return list;
  }, [player?.id, myDisplayName, groupMembers]);

  // This lobby's own Daily room (one per group + scenario, created on demand by
  // the server's presence sweep and stored on the waiting game). Only join once
  // our token was minted for THIS room: during a room rotation the room changes
  // first and the token catches up a tick later, and joining with a token for a
  // different room would simply fail.
  const lobbyRoom = game?.get("lobbyRooms")?.[myGroupName];
  const hasVideoRoom =
    !!lobbyRoom?.url &&
    !!player?.get("dailyMeetingToken") &&
    player?.get("dailyTokenRoom") === lobbyRoom.roomName;
  const hasCompletedIntro = player?.get("introDone");

  // [DIAG] Full lobby snapshot every render: who am I, what game am I attached
  // to, and the roster the client sees.
  console.log("[DIAG][lobby] render", {
    playerId: player?.id,
    gameID: player?.get("gameID"),
    lobbyGameId: game?.id,
    isWaitingGame: game?.get("isWaiting"),
    ended: player?.get("ended"),
    introDone: player?.get("introDone"),
    scenario: player?.get("scenario"),
    myGroupName,
    totalGroupMembers,
    canStartGame,
    rosterIds: Object.keys(waitingPlayersObj),
  });

  // [DIAG] Capture the exact moment the client's game attachment changes (or never
  // does). If gameID/lobbyGameId/isWaiting never change after Confirm, the
  // lobby→game transition is the failure.
  useEffect(() => {
    console.log("[DIAG][lobby] attachment changed", {
      playerId: player?.id,
      gameID: player?.get("gameID"),
      lobbyGameId: game?.id,
      isWaitingGame: game?.get("isWaiting"),
    });
  }, [player?.get("gameID"), game?.id, game?.get("isWaiting")]);

  // [DIAG] Mount/unmount of the lobby itself.
  useEffect(() => {
    console.log("[DIAG][lobby] CustomLobby MOUNTED", { playerId: player?.id });
    return () => console.log("[DIAG][lobby] CustomLobby UNMOUNTED", { playerId: player?.id });
  }, []);

  // Assignment modal state. Clicking Start opens the modal; that player assigns
  // players to rooms by hand (or via Random Assign); Confirm sends the explicit
  // room→player mapping along with requestStart.
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  const openAssignmentModal = () => {
    console.log("[DIAG][lobby] openAssignmentModal click", {
      playerId: player?.id, hasGame: !!game, hasPlayer: !!player,
    });
    if (!game || !player) {
      console.warn("[DIAG][lobby] openAssignmentModal BLOCKED - no game/player");
      return;
    }
    setShowAssignmentModal(true);
  };

  // `assignments` is an array (one entry per game room) of playerId arrays.
  // Players left unassigned are simply omitted — they stay in the lobby.
  const confirmStartGame = (assignments) => {
    console.log("[DIAG][lobby] confirmStartGame click", {
      playerId: player?.id, gameId: game?.id, gameID: player?.get("gameID"), assignments,
    });
    if (!game || !player) {
      console.warn("[DIAG][lobby] confirmStartGame BLOCKED - no game/player");
      return;
    }

    const payload = { groupName: myGroupName, timestamp: Date.now(), assignments };
    console.log("[DIAG][lobby] setting requestStart", payload);

    // Set attribute on PLAYER instead of game (game attribute listeners don't work in Empirica)
    player.set("requestStart", payload);
    console.log("[DIAG][lobby] requestStart set, value now:", player.get("requestStart"));

    setShowAssignmentModal(false);
  };

  return (
    <div className="h-screen w-screen bg-gray-100 flex">
      {showAssignmentModal && (
        <AssignmentModal
          players={assignmentPlayers}
          playerCount={gamePlayerCount}
          onCancel={() => setShowAssignmentModal(false)}
          onConfirm={confirmStartGame}
        />
      )}
      {/* Left sidebar - Info */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col p-6 overflow-y-auto">
        {/* Group header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <Users className="w-10 h-10 text-blue-500" strokeWidth={1.5} />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-1">
            {myDisplayGroupName}
          </h1>
          {/* Two lobbies can share a group name and differ only by scenario, so
              name the scenario. `scenarioNames` is the role JSON's own title
              (e.g. "The Vacation"), resolved server-side; it falls back to the
              slug if that scenario's role data carries no name. */}
          {scenarioTitle && (
            <p className="text-sm text-gray-500">{scenarioTitle}</p>
          )}
        </div>

        {/* Start button — available to every group member */}
        <button
          onClick={openAssignmentModal}
          disabled={!canStartGame}
          className="w-full mb-6 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors"
        >
          <Play className="w-5 h-5" />
          Start Game ({totalGroupMembers} player{totalGroupMembers !== 1 ? 's' : ''})
        </button>
        {!canStartGame && (
          <p className="text-xs text-amber-600 mb-4 -mt-4">
            Need at least 2 players to start
          </p>
        )}

        {/* Group members list */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Members ({groupMembers.length + 1})
          </h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span>{player?.get("displayName") || "You"} (You)</span>
            </div>
            {groupMembers.map(p => (
              <div key={p.id} className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span>{p.displayName || "Anonymous"}</span>
              </div>
            ))}
            {groupMembers.length === 0 && (
              <p className="text-xs text-gray-400 italic">
                Waiting for teammates...
              </p>
            )}
          </div>
        </div>

        {/* Waiting info */}
        <div className="text-sm text-gray-600 mb-6">
          <p className="mb-2">
            Click 'Start Game' when everyone is ready.
          </p>
        </div>

      </div>

      {/* Main area - Video chat */}
      <div className="flex-1 p-4">
        {hasVideoRoom && hasCompletedIntro ? (
          <div className="h-full rounded-lg overflow-hidden">
            <VideoChat
              defaultHideSelf={false}
              filterPlayerIds={groupMemberIds}
              roomUrl={lobbyRoom.url}
            />
          </div>
        ) : (
          <div className="h-full bg-gray-200 rounded-lg flex items-center justify-center">
            <div className="text-center text-gray-500">
              <Video className="w-16 h-16 mx-auto mb-3 opacity-50" />
              <p className="text-lg">
                {!hasCompletedIntro
                  ? "Complete intro to enable video chat"
                  : "Setting up video room..."}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact selectable token for a player sitting inside a room. Draggable too, so
// a placed player can be dragged into another room (or back to Unassigned).
function RoomMemberToken({ player, selected, onSelect, onDragStart, onDragEnd }) {
  return (
    <button
      type="button"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className={
        "flex items-center gap-1.5 rounded-full pl-0.5 pr-2.5 py-0.5 border transition-colors cursor-grab active:cursor-grabbing " +
        (selected
          ? "border-blue-500 ring-2 ring-blue-400 bg-blue-50"
          : "border-gray-200 bg-white hover:bg-gray-100")
      }
    >
      <Avatar player={player} size="w-7 h-7 text-[10px]" />
      <span className="text-xs text-gray-800 max-w-[7rem] truncate">
        {player.displayName}
        {player.isSelf && " (You)"}
      </span>
    </button>
  );
}

// Manual assignment modal. The starting player selects a player, then clicks a room (or
// the unassigned pool) to place them there. Rooms start empty; there are as
// many as could ever be needed — floor(N/2), since a game needs >= 2 players —
// so they can leave some empty if they want fewer/larger games. Anyone left
// in the unassigned pool simply stays in the lobby for this round. Random Assign
// auto-fills the rooms for review.
function AssignmentModal({ players, playerCount, onCancel, onConfirm }) {
  const roomCount = Math.max(1, Math.floor(players.length / 2));

  // assignments: { [playerId]: "g0" | "g1" | ... }. Missing = the unassigned
  // pool (stays in lobby).
  const [assignments, setAssignments] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  // When set, holds the shuffled roster awaiting the starting player's answer to the
  // exact-vs-double-up question before Random Assign fills the rooms.
  const [pendingShuffle, setPendingShuffle] = useState(null);

  // Drop assignments for players who left, or for rooms that no longer exist
  // (the roster can change while the modal is open).
  useEffect(() => {
    const valid = (key) =>
      typeof key === "string" && key[0] === "g" && Number(key.slice(1)) < roomCount;
    setAssignments((prev) => {
      const next = {};
      let changed = false;
      for (const p of players) {
        if (prev[p.id] && valid(prev[p.id])) next[p.id] = prev[p.id];
      }
      for (const k of Object.keys(prev)) {
        if (next[k] !== prev[k]) changed = true;
      }
      if (Object.keys(next).length !== Object.keys(prev).length) changed = true;
      return changed ? next : prev;
    });
  }, [players, roomCount]);

  // --- Drag-and-drop (additive; click+click still works everywhere) ---
  // `dragId` is the player being dragged; `dragOverKey` is the target currently
  // hovered (a room key or "unassigned"), used purely for highlight.
  const [dragId, setDragId] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const handleDragStart = (id) => (e) => {
    setDragId(id);
    setSelectedId(null);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const handleDragEnd = () => {
    setDragId(null);
    setDragOverKey(null);
  };
  // roomKey === null means "drop back into the unassigned pool".
  const dropTo = (roomKey) => (e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragId;
    setDragId(null);
    setDragOverKey(null);
    if (!id) return;
    if (roomKey === null) {
      setAssignments((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } else {
      setAssignments((prev) => ({ ...prev, [id]: roomKey }));
    }
  };
  const allowDrop = (key) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverKey !== key) setDragOverKey(key);
  };
  const handleDragLeave = (key) => (e) => {
    // Ignore leave events fired when moving onto a child element.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverKey((k) => (k === key ? null : k));
  };

  const place = (roomKey) => {
    if (!selectedId) return;
    setAssignments((prev) => ({ ...prev, [selectedId]: roomKey }));
    setSelectedId(null);
  };
  const unassignSelected = () => {
    if (!selectedId) return;
    setAssignments((prev) => {
      const next = { ...prev };
      delete next[selectedId];
      return next;
    });
    setSelectedId(null);
  };
  const toggleSelect = (id) => setSelectedId((s) => (s === id ? null : id));

  const unassigned = players.filter((p) => !assignments[p.id]);
  const gameRooms = Array.from({ length: roomCount }, (_, i) =>
    players.filter((p) => assignments[p.id] === `g${i}`)
  );

  const startableGames = gameRooms.filter((g) => g.length >= 2);
  const hasUnderfilled = gameRooms.some((g) => g.length === 1);
  // Unassigned players are allowed — they simply stay in the lobby for this
  // round. The only hard requirements are at least one real game and no solo
  // game rooms.
  const canConfirm = startableGames.length >= 1 && !hasUnderfilled;

  const validationMessage = hasUnderfilled
    ? "Each game needs at least 2 players. Move or remove any solo player."
    : startableGames.length === 0
      ? "Put at least one game room together to start."
      : null;

  // Soft warnings — allowed, but worth a confirmation before starting. These are
  // distinct from the hard blocks above (solo rooms / no games at all).
  const smallGames = startableGames.filter((g) => g.length < playerCount).length;
  const bigGames = startableGames.filter((g) => g.length > playerCount).length;
  const warnings = [];
  if (unassigned.length > 0) {
    warnings.push(
      `${unassigned.length} player${unassigned.length === 1 ? "" : "s"} will be left unassigned and stay in the lobby.`
    );
  }
  if (smallGames > 0) {
    warnings.push(
      `${smallGames} game${smallGames === 1 ? "" : "s"} ${smallGames === 1 ? "has" : "have"} fewer than the recommended ${playerCount} players.`
    );
  }
  if (bigGames > 0) {
    warnings.push(
      `${bigGames} game${bigGames === 1 ? "" : "s"} ${bigGames === 1 ? "has" : "have"} more than the recommended ${playerCount} players.`
    );
  }

  // When set, the warning confirmation dialog is shown before the actual start.
  const [showWarnings, setShowWarnings] = useState(false);

  const doConfirm = () => {
    setShowWarnings(false);
    onConfirm(gameRooms.filter((g) => g.length >= 2).map((g) => g.map((p) => p.id)));
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    if (warnings.length > 0) {
      setShowWarnings(true);
      return;
    }
    doConfirm();
  };

  const applyResult = (result) => {
    const next = {};
    result.games.forEach((g, i) => g.forEach((p) => (next[p.id] = `g${i}`)));
    // Leftovers stay unassigned (no entry) — they remain in the lobby pool.
    setAssignments(next);
    setSelectedId(null);
    setPendingShuffle(null);
  };

  const runRandom = () => {
    const shuffled = shuffle(players);
    const exact = chunkExact(shuffled, playerCount);
    // Only ask the exact-vs-double-up question when it actually matters — i.e.
    // when the players don't divide evenly into games of the configured size.
    if (exact.leftovers.length > 0) {
      setPendingShuffle(shuffled);
    } else {
      applyResult(exact);
    }
  };

  const capacity = Math.max(2, playerCount);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-[100px]">
      <div className="bg-white rounded-xl shadow-2xl w-full h-full flex flex-col">
        {/* Header: the instructions are the title, close X on the right */}
        <div className="flex items-start justify-between px-6 py-4 bg-blue-50 border-b border-blue-200 rounded-t-xl">
          <p className="text-2xl font-bold text-blue-900 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>To assign players:</span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-lg">1</span>
              click a player
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-lg">2</span>
              click a room
            </span>
          </p>
          <button
            onClick={onCancel}
            className="p-1 rounded hover:bg-blue-100 text-blue-700 flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-6 px-6 py-3 border-b border-gray-200 bg-gray-50">
          <span className="text-lg text-gray-900 flex-shrink-0"><strong>Players per game:</strong> {playerCount}</span>
          <span className="text-lg text-gray-900 flex-shrink-0"><strong>Total players:</strong> {players.length}</span>
          {players.length % playerCount !== 0 && (
            <div className="flex items-center gap-2 text-amber-700 min-w-0">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-500" strokeWidth={2} />
              <span className="text-xs leading-tight">
                {players.length < playerCount ? (
                  <>
                    You will need to find {playerCount - players.length} more player{playerCount - players.length === 1 ? "" : "s"},
                    or complete the exercise with an incomplete role set. We strongly
                    recommend you complete the exercise with all roles filled.
                  </>
                ) : (
                  <>
                    You have an odd number of players, and will need to either overfill
                    or leave someone in the lobby.
                  </>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Body: unassigned pool + rooms */}
        <div className="flex-1 overflow-y-auto flex min-h-0">
          {/* Unassigned pool */}
          <div
            onClick={unassignSelected}
            onDragOver={allowDrop("unassigned")}
            onDragLeave={handleDragLeave("unassigned")}
            onDrop={dropTo(null)}
            className={
              "w-64 flex-shrink-0 border-r border-gray-200 p-4 overflow-y-auto transition-colors " +
              (dragOverKey === "unassigned" ? "bg-blue-50 " : "") +
              (selectedId ? "cursor-pointer hover:bg-blue-50/40" : "")
            }
          >
            <button
              onClick={(e) => { e.stopPropagation(); runRandom(); }}
              className="w-full mb-4 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2.5 rounded-lg shadow-sm transition-colors"
            >
              <Shuffle className="w-5 h-5" />
              Random Assign
            </button>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Unassigned ({unassigned.length})
            </h3>
            <div className="space-y-2">
              {unassigned.map((p) => (
                <div key={p.id} onClick={(e) => e.stopPropagation()}>
                  <PlayerChip
                    player={p}
                    selected={selectedId === p.id}
                    onClick={() => toggleSelect(p.id)}
                    onDragStart={handleDragStart(p.id)}
                    onDragEnd={handleDragEnd}
                  />
                </div>
              ))}
              {unassigned.length === 0 && (
                <p className="text-xs text-gray-400 italic">Everyone is assigned.</p>
              )}
            </div>
          </div>

          {/* Rooms */}
          <div className="flex-1 p-4 overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {gameRooms.map((members, idx) => {
                const overfilled = members.length > playerCount;
                const underfilled = members.length === 1;
                const emptySlots = Math.max(0, capacity - members.length);
                return (
                  <div
                    key={idx}
                    onClick={() => place(`g${idx}`)}
                    onDragOver={allowDrop(`g${idx}`)}
                    onDragLeave={handleDragLeave(`g${idx}`)}
                    onDrop={dropTo(`g${idx}`)}
                    className={
                      "border rounded-lg p-3 bg-white transition-colors min-h-[5.5rem] " +
                      (dragOverKey === `g${idx}`
                        ? "border-blue-500 bg-blue-50/60 "
                        : selectedId
                        ? "cursor-pointer border-blue-300 hover:border-blue-500 hover:bg-blue-50/40 "
                        : "border-gray-200 ") +
                      (underfilled ? "ring-1 ring-amber-300" : "")
                    }
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Game {idx + 1}
                      </span>
                      <span className="text-xs text-gray-400">
                        {members.length}/{playerCount}
                        {overfilled && (
                          <span className="ml-1 text-amber-600">overfilled</span>
                        )}
                        {underfilled && (
                          <span className="ml-1 text-amber-600">needs 2+</span>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {members.map((p) => (
                        <RoomMemberToken
                          key={p.id}
                          player={p}
                          selected={selectedId === p.id}
                          onSelect={() => toggleSelect(p.id)}
                          onDragStart={handleDragStart(p.id)}
                          onDragEnd={handleDragEnd}
                        />
                      ))}
                      {Array.from({ length: emptySlots }, (_, i) => (
                        <Avatar key={`e${i}`} empty />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <p className="text-xs text-amber-600 min-h-[1rem]">{validationMessage}</p>
          <div className="flex gap-3 flex-shrink-0">
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Play className="w-4 h-4" />
              Confirm and Start
            </button>
          </div>
        </div>
      </div>

      {pendingShuffle && (players.length < playerCount ? (
        // Underfill: too few players for even one full game, so there are no
        // options to pick between — just warn, then put everyone in one room.
        <UnderfillWarningDialog
          missing={playerCount - players.length}
          onCancel={() => setPendingShuffle(null)}
          onConfirm={() => applyResult(chunkOverfill(pendingShuffle, playerCount))}
        />
      ) : (
        <RandomOptionsDialog
          onCancel={() => setPendingShuffle(null)}
          onExact={() => applyResult(chunkExact(pendingShuffle, playerCount))}
          onDoubleUp={() => applyResult(chunkOverfill(pendingShuffle, playerCount))}
        />
      ))}

      {showWarnings && (
        <StartWarningDialog
          warnings={warnings}
          onCancel={() => setShowWarnings(false)}
          onConfirm={doConfirm}
        />
      )}
    </div>
  );
}

// Shown when starting with non-blocking issues (unassigned players, or
// games smaller/larger than recommended). Lists each issue and lets them go
// back and fix it, or start anyway.
function StartWarningDialog({ warnings, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-8 h-8 text-amber-500 flex-shrink-0" strokeWidth={2} />
          <h3 className="text-lg font-semibold text-gray-900 mt-0.5">
            Start with these issues?
          </h3>
        </div>
        <ul className="space-y-2 mb-6">
          {warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-100 transition-colors"
          >
            Go back
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors flex items-center gap-2"
          >
            <Play className="w-4 h-4" />
            Start anyway
          </button>
        </div>
      </div>
    </div>
  );
}

// Shown by Random Assign when there aren't enough players for even one full
// game. No options to choose from — just a warning, then everyone goes into a
// single (incomplete) room.
function UnderfillWarningDialog({ missing, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-8 h-8 text-amber-500 flex-shrink-0" strokeWidth={2} />
          <h3 className="text-lg font-semibold text-gray-900 mt-0.5">
            Not enough players for a full game
          </h3>
        </div>
        <p className="text-sm text-gray-700 mb-6">
          You will need to find {missing} more player{missing === 1 ? "" : "s"},
          or complete the exercise with an incomplete role set. We strongly
          recommend you complete the exercise with all roles filled.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-100 transition-colors"
          >
            Go back
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors"
          >
            Assign anyway
          </button>
        </div>
      </div>
    </div>
  );
}

// Asked when Random Assign can't split everyone into evenly-sized games: either
// keep games exact (extras wait in the lobby) or double up roles so everyone
// gets into a game.
function RandomOptionsDialog({ onCancel, onExact, onDoubleUp }) {
  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          The players don't divide evenly
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Would you like to exactly match the number of people required for each
          game, or double up roles to get everyone into a game?
        </p>
        <div className="space-y-3">
          <button
            onClick={onExact}
            className="w-full text-left border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50/40 transition-colors"
          >
            <div className="font-semibold text-gray-900">Exact match</div>
            <div className="text-sm text-gray-500 mt-0.5">
              Make full games only. Anyone left over waits in the lobby.
            </div>
          </button>
          <button
            onClick={onDoubleUp}
            className="w-full text-left border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50/40 transition-colors"
          >
            <div className="font-semibold text-gray-900">Double up roles</div>
            <div className="text-sm text-gray-500 mt-0.5">
              Get everyone into a game by adding extra players to a group.
            </div>
          </button>
        </div>
        <div className="flex justify-end mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
